// THE APP'S `SatelliteMapCard` ON THE WEB (WEB-PARITY-1C, DT-2) — see `services/satellite.ts` for what it draws and
// what it sends.
//
// IT IS THERE, OR IT IS NOT. The app shows a tinted box with a spinner while it loads (SatelliteMapCard.swift:49-54) and
// `EmptyView()` when it cannot draw (:55-56): no placeholder text, no error. So here — and the card is not even begun
// until the token function has said this site can draw one, so a site with no MapKit key (staging, today) never shows a
// box that then vanishes.

import { useEffect, useRef, useState } from "react";
import { useSession } from "../app/session";
import type { Coordinates } from "../services/satellite";
import { words } from "../routes/steps/detailsWording";

type Phase = "checking" | "loading" | "shown" | "failed";

export function SatelliteImage({ at }: { at: Coordinates }) {
  const view = useSession().satelliteView;
  const [phase, setPhase] = useState<Phase>(view === null ? "failed" : "checking");
  const box = useRef<HTMLDivElement>(null);
  const { latitude, longitude } = at;

  useEffect(() => {
    if (view === null) return;
    let alive = true;
    void view.available().then((ok) => {
      if (alive) setPhase(ok ? "loading" : "failed");
    });
    return () => {
      alive = false;
    };
  }, [view]);

  const drawing = phase === "loading" || phase === "shown";
  useEffect(() => {
    if (view === null || !drawing || box.current === null) return;
    let alive = true;
    let takeDown: (() => void) | null = null;
    view.draw(box.current, { latitude, longitude }, () => {
      if (alive) setPhase("failed");
    }).then(
      (down) => {
        if (!alive) {
          down();
          return;
        }
        takeDown = down;
        setPhase("shown");
      },
      () => {
        if (alive) setPhase("failed");
      },
    );
    return () => {
      alive = false;
      takeDown?.();
    };
  }, [view, drawing, latitude, longitude]);

  if (!drawing) return null;
  return (
    <figure className="satellite" data-satellite={phase}>
      <div className="satellite-map" ref={box} />
      {phase === "loading" && <span className="satellite-spinner" aria-hidden="true" />}
      {/* SatelliteMapCard.swift:40 — the caption and its globe, the app's word. */}
      <figcaption className="satellite-caption" data-held-copy="details.satellite.caption">
        <GlobeIcon />
        {words("details.satellite.caption")}
      </figcaption>
    </figure>
  );
}

function GlobeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M1 8h14M8 1c2 2.2 2.9 4.5 2.9 7S10 12.8 8 15M8 1C6 3.2 5.1 5.5 5.1 8S6 12.8 8 15" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}
